import { useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Gauge, AlertTriangle, FileText, TrendingUp, TrendingDown, Minus, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import logger from "@/lib/logger";

const MODULE_ROUTES = {
  qms_documents:  "/documents",
  risk_register:  "/risk",
  iec62304:       "/iec62304",
  cybersecurity:  "/cybersecurity",
  ai_governance:  "/ai-governance",
  k510_checklist: "/submissions",
  clinical:       "/clinical",
  training:       "/training/matrix",
};

const MODULE_LABELS = {
  qms_documents:   "QMS Documents",
  risk_register:   "Risk Register (ISO 14971)",
  iec62304:        "IEC 62304 Software Lifecycle",
  cybersecurity:   "Cybersecurity",
  ai_governance:   "AI/ML Governance",
  k510_checklist:  "510(k) Submission Checklist",
  clinical:        "Clinical Evidence",
  training:        "Training (ISO 13485 §6.2)",
};

const BUCKET_META = {
  ready_for_review: { label: "Ready for reviewer", color: "#059669", bg: "#D1FAE5" },
  on_track:         { label: "On track", color: "#0055FF", bg: "#DBEAFE" },
  early_stage:      { label: "Early stage", color: "#D97706", bg: "#FEF3C7" },
  not_started:      { label: "Not started", color: "#DC2626", bg: "#FEE2E2" },
};

function scoreColor(pct) {
  if (pct >= 85) return "#059669";
  if (pct >= 60) return "#0055FF";
  if (pct >= 30) return "#D97706";
  return "#DC2626";
}

export default function RegulatoryReadiness() {
  const { loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [briefing, setBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    api.get("/devices").then(({ data }) => {
      setProjects(data);
      if (data[0]) setPid(data[0].project_id);
    }).catch((e) => logger.warn("Load projects:", e?.response?.status));
  }, [authLoading]);

  const load = useCallback(() => {
    if (!pid) return;
    setLoading(true);
    api.get(`/regulatory-readiness/${pid}`)
      .then(({ data }) => setData(data))
      .catch((e) => logger.warn("Readiness:", e?.response?.status))
      .finally(() => setLoading(false));
    setBriefing(null);
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const openBriefing = async () => {
    if (!pid) return;
    setBriefingLoading(true);
    try {
      const { data } = await api.get(`/regulatory-readiness/${pid}/briefing`);
      setBriefing(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Briefing failed."); }
    finally { setBriefingLoading(false); }
  };

  const downloadBriefingPdf = async () => {
    if (!pid) return;
    try {
      const res = await fetch(`${API}/regulatory-readiness/${pid}/briefing.pdf`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `briefing_${pid}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(`Download failed: ${e.message}`); }
  };

  const downloadBinder = async (kind) => {
    if (!pid) return;
    try {
      const path = kind === "pdf" ? `pdf` : `download`;
      const ext = kind === "pdf" ? "pdf" : "zip";
      const res = await fetch(`${API}/audit-binder/${pid}/${path}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit_binder_${pid}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(`Download failed: ${e.message}`); }
  };

  const bucket = data ? BUCKET_META[data.bucket] || BUCKET_META.not_started : null;

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="mb-6">
          <div className="vq-overline mb-2 flex items-center gap-2"><Gauge className="w-3 h-3" /> Regulatory Readiness</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900">
            Readiness Score
          </h1>
          <p className="text-slate-600 mt-2 max-w-3xl">
            One number, per device project, aggregating readiness across every VitaQMS module.
            Advisory only — a qualified reviewer must confirm actual submission readiness.
          </p>
        </div>

        <div className="mb-6 max-w-md">
          <Label className="text-xs font-mono uppercase tracking-wider text-slate-500">Device project</Label>
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger data-testid="readiness-project-select"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              {projects.map((p) => <SelectItem key={p.project_id} value={p.project_id}>{p.device_name || p.project_id}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading && <div className="text-sm text-slate-500">Computing…</div>}
        {data && (
          <>
            <div className="vq-card p-6 mb-4" data-testid="readiness-panel">
              <div className="flex items-center gap-6 flex-wrap">
                <div className="relative w-40 h-40 flex-shrink-0">
                  <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                    <circle cx="50" cy="50" r="42" fill="none" stroke="#E2E8F0" strokeWidth="10" />
                    <circle cx="50" cy="50" r="42" fill="none"
                            stroke={scoreColor(data.overall_score)} strokeWidth="10"
                            strokeDasharray={`${(data.overall_score / 100) * 264} 264`}
                            strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className="font-display font-extrabold text-4xl text-slate-900" data-testid="readiness-overall">
                      {data.overall_score.toFixed(0)}
                    </div>
                    <div className="text-xs text-slate-500 font-mono uppercase tracking-wider">of 100</div>
                  </div>
                </div>
                <div className="flex-1 min-w-64">
                  <div className="text-xs font-mono uppercase tracking-wider text-slate-500">Status</div>
                  <div className="inline-block mt-1 text-sm px-3 py-1 rounded-full font-semibold"
                       style={{ backgroundColor: bucket.bg, color: bucket.color }}
                       data-testid="readiness-bucket">
                    {bucket.label}
                  </div>
                  <div className="font-display font-bold text-slate-900 mt-3">{data.device_name}</div>
                  <div className="text-xs text-slate-500 font-mono mt-1">
                    Computed {new Date(data.computed_at).toLocaleString()}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" data-testid="open-briefing-btn"
                            onClick={openBriefing} disabled={briefingLoading}>
                      <FileText className="w-3 h-3 mr-1" />
                      {briefingLoading ? "Loading…" : "Weekly briefing"}
                    </Button>
                    <Button size="sm" variant="outline" data-testid="download-briefing-pdf-btn"
                            onClick={downloadBriefingPdf}>
                      <FileText className="w-3 h-3 mr-1" /> Briefing PDF
                    </Button>
                    <Button size="sm" variant="outline" data-testid="download-binder-zip-btn"
                            onClick={() => downloadBinder("download")}>
                      <FileText className="w-3 h-3 mr-1" /> Audit binder ZIP
                    </Button>
                    <Button size="sm" variant="outline" data-testid="download-binder-pdf-btn"
                            onClick={() => downloadBinder("pdf")}>
                      <FileText className="w-3 h-3 mr-1" /> Audit binder PDF
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(data.modules).map(([k, v]) => (
                <div key={k} className="vq-card p-4" data-testid={`readiness-module-${k}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">{MODULE_LABELS[k] || k}</div>
                      <div className="text-xs text-slate-500 mt-0.5">Weight {(data.weights[k] * 100).toFixed(0)}%</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="font-display font-extrabold text-2xl" style={{ color: scoreColor(v) }}>
                        {v.toFixed(0)}
                      </div>
                      {MODULE_ROUTES[k] && (
                        <Button size="sm" variant="ghost" data-testid={`open-module-${k}`}
                                onClick={() => navigate(MODULE_ROUTES[k])}>
                          <ArrowRight className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="h-2 bg-slate-100 rounded">
                    <div className="h-full rounded"
                         style={{ width: `${v}%`, backgroundColor: scoreColor(v) }} />
                  </div>
                </div>
              ))}
            </div>

            <div className="vq-card p-4 mt-4 border-l-4 border-l-amber-500 bg-amber-50 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-amber-900">{data.disclaimer}</div>
            </div>

            {briefing && (
              <div className="vq-card p-5 mt-4" data-testid="briefing-panel">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="vq-overline">Weekly Regulatory Briefing</div>
                    <div className="font-display font-bold text-slate-900 mt-1">Week {briefing.week_iso}</div>
                    <div className="text-xs text-slate-500 mt-0.5">Generated {new Date(briefing.generated_at).toLocaleString()}</div>
                  </div>
                  <BriefingDelta delta={briefing.overall_delta} prior={briefing.prior_snapshot_date} />
                </div>

                <div className="mt-4">
                  <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Module deltas (7d)</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="briefing-module-deltas">
                    {Object.entries(briefing.module_deltas).map(([k, d]) => (
                      <div key={k} className="text-xs border border-slate-200 rounded-md p-2">
                        <div className="text-slate-600">{MODULE_LABELS[k] || k}</div>
                        <div className={
                          d === null ? "text-slate-400 mt-0.5" :
                          d > 0 ? "text-emerald-700 font-semibold mt-0.5" :
                          d < 0 ? "text-red-600 font-semibold mt-0.5" : "text-slate-500 mt-0.5"
                        }>
                          {d === null ? "no prior snapshot" : (d > 0 ? `+${d.toFixed(1)}` : d.toFixed(1))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4">
                  <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Top 3 improvements this week</div>
                  {briefing.top_levers.length === 0 ? (
                    <div className="text-sm text-emerald-700">Every module is at 100. Well done.</div>
                  ) : (
                    <ol className="space-y-2" data-testid="briefing-levers">
                      {briefing.top_levers.map((lv, i) => (
                        <li key={lv.module} className="text-sm flex items-start gap-2">
                          <span className="font-bold text-slate-400 w-5">{i + 1}.</span>
                          <div className="flex-1">
                            <span className="font-semibold text-slate-900">{MODULE_LABELS[lv.module] || lv.module}</span>
                            <span className="text-slate-600"> — {lv.current_score.toFixed(1)} → 100 could add </span>
                            <span className="font-semibold text-emerald-700">+{lv.potential_gain.toFixed(1)}</span>
                            <span className="text-slate-600"> overall points.</span>
                          </div>
                          {MODULE_ROUTES[lv.module] && (
                            <Button size="sm" variant="outline"
                                    data-testid={`open-fix-${lv.module}`}
                                    onClick={() => navigate(MODULE_ROUTES[lv.module])}>
                              Open <ArrowRight className="w-3 h-3 ml-1" />
                            </Button>
                          )}
                        </li>
                      ))}
                    </ol>
                  )}
                </div>

                <div className="mt-4">
                  <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">Overdue vigilance windows (AE / SAE)</div>
                  {briefing.overdue_vigilance.length === 0 ? (
                    <div className="text-sm text-emerald-700">No overdue vigilance windows.</div>
                  ) : (
                    <ul className="text-xs text-slate-700 space-y-1" data-testid="briefing-vigilance">
                      {briefing.overdue_vigilance.map((ae) => (
                        <li key={ae.ae_id}>
                          <span className="font-mono">{ae.ae_id}</span> — study {ae.study_id}
                          {ae.is_sae && <span className="ml-1 text-red-600 font-semibold">SAE</span>}
                          {ae.overdue_windows?.length > 0 && (
                            <span className="text-red-600 ml-1">overdue: {ae.overdue_windows.join(", ")}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}

function BriefingDelta({ delta, prior }) {
  if (delta === null || delta === undefined) {
    return (
      <div className="text-xs text-slate-500 text-right" data-testid="briefing-delta-none">
        <div>No prior snapshot yet</div>
        <div className="mt-0.5">A week-over-week comparison will appear once ≥ 7 days of data exist.</div>
      </div>
    );
  }
  const Icon = delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  const color = delta > 0 ? "#059669" : delta < 0 ? "#DC2626" : "#6B7280";
  return (
    <div className="text-right" data-testid="briefing-delta">
      <div className="inline-flex items-center gap-1 font-semibold" style={{ color }}>
        <Icon className="w-4 h-4" />
        {delta > 0 ? "+" : ""}{delta.toFixed(1)} pts
      </div>
      <div className="text-xs text-slate-500 mt-0.5">vs {prior}</div>
    </div>
  );
}

